/* Immer relies on these 'dirty' tricks: https://redux-toolkit.js.org/api/createSlice#examples
 * note that "state" is already a draft of the original state and meant to be reassigned */
import { createSlice } from '@reduxjs/toolkit';
import {
  createPromptConfig,
  createPromptConfigVersion,
  getAllPromptConfigs,
  getAllCampaignReportPromptContents,
  getPromptConfigById,
  getAllPromptConfigSectionEvaluators,
} from 'features/prompt-management/async';
import { PromptManagementState } from 'features/prompt-management/types';
import {
  fulfilled,
  initialRequestState,
  pending,
  rejected,
  requestReducer,
} from 'features/shared/request';

export const initialState: PromptManagementState = {
  promptSectionEvaluators: {
    data: undefined,
    request: initialRequestState,
  },
  prompts: {
    data: undefined,
    request: initialRequestState,
  },
  currentPrompt: {
    data: undefined,
    request: initialRequestState,
  },
  promptContents: {
    data: undefined,
    request: initialRequestState,
  },
};

const slice = createSlice({
  name: 'promptManagement',
  initialState,
  reducers: {
    resetPrompts(state) {
      state.prompts = {
        data: undefined,
        request: initialRequestState,
      };
      state.currentPrompt = {
        data: undefined,
        request: initialRequestState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getAllPromptConfigSectionEvaluators.fulfilled,
      (state, { meta, payload }) => {
        if (state.promptSectionEvaluators.request.id === meta.requestId) {
          state.promptSectionEvaluators.data = payload;
          state.promptSectionEvaluators.request = requestReducer(
            state.promptSectionEvaluators.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(
      getAllPromptConfigSectionEvaluators.pending,
      (state, { meta }) => {
        state.promptSectionEvaluators.data = undefined;
        state.promptSectionEvaluators.request = requestReducer(
          state.promptSectionEvaluators.request,
          pending({ meta })
        );
      }
    );
    builder.addCase(
      getAllPromptConfigSectionEvaluators.rejected,
      (state, { meta }) => {
        state.promptSectionEvaluators.data = undefined;
        state.promptSectionEvaluators.request = requestReducer(
          state.promptSectionEvaluators.request,
          rejected({ meta })
        );
      }
    );

    builder.addCase(
      getAllPromptConfigs.fulfilled,
      (state, { meta, payload }) => {
        if (state.prompts.request.id === meta.requestId) {
          state.prompts.data = payload;
          state.prompts.request = requestReducer(
            state.prompts.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAllPromptConfigs.pending, (state, { meta }) => {
      state.prompts.data = undefined;
      state.prompts.request = requestReducer(
        state.prompts.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllPromptConfigs.rejected, (state, { meta }) => {
      state.prompts.data = undefined;
      state.prompts.request = requestReducer(
        state.prompts.request,
        rejected({ meta })
      );
    });

    builder.addCase(
      getPromptConfigById.fulfilled,
      (state, { meta, payload }) => {
        if (state.currentPrompt.request.id === meta.requestId) {
          state.currentPrompt.data = payload;
          state.currentPrompt.request = requestReducer(
            state.currentPrompt.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getPromptConfigById.pending, (state, { meta }) => {
      state.currentPrompt.data = undefined;
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        pending({ meta })
      );
    });
    builder.addCase(getPromptConfigById.rejected, (state, { meta }) => {
      state.currentPrompt.data = undefined;
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        rejected({ meta })
      );
    });

    builder.addCase(createPromptConfig.pending, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        pending({ meta })
      );
    });
    builder.addCase(createPromptConfig.fulfilled, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        fulfilled({ meta })
      );
    });
    builder.addCase(createPromptConfig.rejected, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        rejected({ meta })
      );
    });

    builder.addCase(createPromptConfigVersion.pending, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        pending({ meta })
      );
    });
    builder.addCase(createPromptConfigVersion.fulfilled, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        fulfilled({ meta })
      );
    });
    builder.addCase(createPromptConfigVersion.rejected, (state, { meta }) => {
      state.currentPrompt.request = requestReducer(
        state.currentPrompt.request,
        rejected({ meta })
      );
    });

    builder.addCase(
      getAllCampaignReportPromptContents.fulfilled,
      (state, { meta, payload }) => {
        if (state.promptContents.request.id === meta.requestId) {
          state.promptContents.data = payload;
          state.promptContents.request = requestReducer(
            state.promptContents.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(
      getAllCampaignReportPromptContents.pending,
      (state, { meta }) => {
        state.promptContents.data = undefined;
        state.promptContents.request = requestReducer(
          state.promptContents.request,
          pending({ meta })
        );
      }
    );
    builder.addCase(
      getAllCampaignReportPromptContents.rejected,
      (state, { meta }) => {
        state.promptContents.data = undefined;
        state.promptContents.request = requestReducer(
          state.promptContents.request,
          rejected({ meta })
        );
      }
    );
  },
});

export const { resetPrompts } = slice.actions;

const { reducer } = slice;

export default reducer;
